(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.DaLucaCartEditPlan = factory();
})(typeof globalThis === 'object' ? globalThis : this, function () {
  'use strict';

  const TOKEN = '_DaLuca edit token';
  const PARENT = '_gpo_product_group';
  const CHILD = '_gpo_parent_product_group';
  const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

  function fail(code, detail) {
    const error = new Error(code + (detail ? ': ' + detail : ''));
    error.code = code;
    throw error;
  }

  function stable(value) {
    if (Array.isArray(value)) return value.map(stable);
    if (value && typeof value === 'object') {
      const result = Object.create(null);
      Object.keys(value).sort().forEach(key => {
        if (value[key] !== undefined) result[key] = stable(value[key]);
      });
      return result;
    }
    return value;
  }

  function signature(properties) {
    return JSON.stringify(stable(properties || {}));
  }

  function sellingPlan(line) {
    return String(line.selling_plan_allocation?.selling_plan?.id ?? line.selling_plan ?? '');
  }

  function fingerprint(line) {
    return JSON.stringify([
      String(line.product_id ?? ''),
      String(line.variant_id ?? line.id ?? ''),
      sellingPlan(line),
      signature(line.properties)
    ]);
  }

  function reference(line) {
    return {
      key: String(line.key),
      product_id: String(line.product_id ?? ''),
      variant_id: String(line.variant_id ?? line.id ?? ''),
      properties: JSON.parse(signature(line.properties)),
      selling_plan: sellingPlan(line),
      quantity: Number(line.quantity)
    };
  }

  function lines(cart) {
    if (!cart || !Array.isArray(cart.items)) fail('INVALID_CART');
    const keys = new Set();
    cart.items.forEach(line => {
      if (!line || !line.key || keys.has(String(line.key)) ||
          !Number.isSafeInteger(Number(line.quantity)) || Number(line.quantity) <= 0 ||
          !String(line.product_id ?? '') || !String(line.variant_id ?? line.id ?? '')) {
        fail('INVALID_CART');
      }
      keys.add(String(line.key));
    });
    return cart.items;
  }

  function identify(cart, ref, allowQuantityChange) {
    const items = lines(cart);
    if (typeof ref === 'string') return items.find(line => String(line.key) === ref) || null;
    if (!ref || typeof ref !== 'object') fail('INVALID_REFERENCE');
    const same = line => fingerprint(line) === fingerprint(ref);
    const exact = items.find(line => String(line.key) === String(ref.key));
    if (exact) {
      if (!same(exact) || (!allowQuantityChange && ref.quantity != null && Number(exact.quantity) !== Number(ref.quantity))) {
        fail('CART_CHANGED');
      }
      return exact;
    }
    const candidates = items.filter(same).filter(line =>
      allowQuantityChange || ref.quantity == null || Number(line.quantity) === Number(ref.quantity)
    );
    if (candidates.length > 1) fail('AMBIGUOUS_LINE');
    if (!candidates.length && items.some(same)) fail('CART_CHANGED');
    return candidates[0] || null;
  }

  function group(line, property) {
    return String(line.properties?.[property] ?? '');
  }

  function buckets(items) {
    const result = new Map();
    items.forEach(line => {
      const key = fingerprint(line);
      if (!result.has(key)) result.set(key, { quantity: 0, items: [] });
      const bucket = result.get(key);
      bucket.quantity += Number(line.quantity);
      if (!Number.isSafeInteger(bucket.quantity)) fail('INVALID_CART');
      bucket.items.push(line);
    });
    return result;
  }

  function tokenItems(items, token) {
    if (typeof token !== 'string' || !token.trim()) fail('INVALID_TOKEN');
    return items.filter(line => String(line.properties?.[TOKEN] ?? '') === token);
  }

  function originalLine(before, originalKey) {
    const original = identify(before, originalKey);
    if (!original) fail('ORIGINAL_MISSING');
    if (group(original, CHILD)) fail('UNSUPPORTED_CHILD_ITEM');
    return original;
  }

  function assertNoLinkedUpgrade(items, original) {
    const optionSignature = signature(original.properties);
    const careHandles = new Set(['venetian-cream-3oz', 'venetian-cream-4oz', 'venetian-cream-leather-shine-cloth', 'extra-standard-spring-bars', 'spring-bar-buckle-removal-tool', 'spring-bar-tool']);
    items.forEach(line => {
      const properties = line.properties || {};
      if (careHandles.has(line.handle) && !properties._gpo_parent_product_group && !line.parent_relationship?.parent_key) return;
      if (String(properties['_DaLuca linked upgrade']) === 'true' &&
          String(properties['_DaLuca parent variant']) === String(original.variant_id ?? original.id)) {
        let matches = true;
        try { matches = signature(JSON.parse(properties['_DaLuca parent options'])) === optionSignature; } catch (_) {}
        if (matches) fail('UNSUPPORTED_LINKED_UPGRADE');
      }
      if (String(properties['_DaLuca linked upgrade']) !== 'true' &&
          /^Quick-Release Spring Bar Upgrade$/i.test(line.product_title || '') &&
          properties['For strap'] && properties['For strap'] === original.product_title) {
        fail('UNSUPPORTED_LINKED_UPGRADE');
      }
    });
  }

  function assertBaselineRetained(beforeItems, afterItems, addedParents, groups) {
    const parents = new Set(addedParents);
    const prior = buckets(beforeItems);
    const current = buckets(afterItems.filter(line => !parents.has(line)));
    const ownedChild = line => groups.has(group(line, CHILD)) && !group(line, PARENT);
    prior.forEach((bucket, key) => {
      const next = current.get(key);
      if (!next || next.quantity < bucket.quantity) fail('CART_CHANGED');
      if (next.quantity !== bucket.quantity && !bucket.items.every(ownedChild)) fail('CART_CHANGED');
    });
    current.forEach((bucket, key) => {
      if (!prior.has(key) && !bucket.items.every(ownedChild)) fail('UNEXPECTED_CART_ITEM');
    });
  }

  function expectedItems(items, updates) {
    return items.map(line => ({
      ref: reference(line),
      quantity: own(updates, String(line.key)) ? updates[String(line.key)] : Number(line.quantity)
    }));
  }

  function validateUpdates(items, updates) {
    Object.keys(updates).forEach(key => {
      const line = items.find(item => String(item.key) === key);
      if (!line || !Number.isSafeInteger(updates[key]) || updates[key] < 0 || updates[key] > Number(line.quantity)) {
        fail('UNSAFE_UPDATE');
      }
    });
  }

  function planCommit(beforeCart, afterCart, originalKey, token) {
    const before = lines(beforeCart);
    const after = lines(afterCart);
    const original = originalLine(beforeCart, originalKey);
    if (tokenItems(before, token).length) fail('TOKEN_REUSED');
    assertNoLinkedUpgrade(before, original);
    const originals = identify(afterCart, reference(original));
    if (!originals) fail('ORIGINAL_MISSING');
    const added = tokenItems(after, token);
    if (!added.length) fail('REPLACEMENT_MISSING');
    if (added.length !== 1) fail('REPLACEMENT_AMBIGUOUS');
    const replacement = added[0];
    if (group(replacement, CHILD) || Number(replacement.quantity) !== Number(original.quantity) ||
        String(replacement.product_id) !== String(original.product_id) ||
        sellingPlan(replacement) !== sellingPlan(original)) {
      fail('REPLACEMENT_MISMATCH');
    }
    const newGroup = group(replacement, PARENT);
    const groups = new Set(newGroup ? [newGroup] : []);
    assertBaselineRetained(before, after, added, groups);
    const updates = Object.create(null);
    updates[String(originals.key)] = 0;
    const oldGroup = group(original, PARENT);
    if (oldGroup) {
      const groupParents = before.filter(line => group(line, PARENT) === oldGroup && !group(line, CHILD));
      const parentQuantity = groupParents.reduce((sum, line) => sum + Number(line.quantity), 0);
      if (!Number.isSafeInteger(parentQuantity) || parentQuantity < Number(original.quantity)) fail('UNSUPPORTED_GPO_ALLOCATION');
      before.filter(line => group(line, CHILD) === oldGroup).forEach(child => {
        if (group(child, PARENT)) fail('UNSUPPORTED_GPO_ALLOCATION');
        const numerator = Number(child.quantity) * Number(original.quantity);
        if (!Number.isSafeInteger(numerator) || numerator % parentQuantity !== 0) fail('UNSUPPORTED_GPO_ALLOCATION');
        const share = numerator / parentQuantity;
        const current = identify(afterCart, reference(child), true);
        if (!current || Number(current.quantity) < Number(child.quantity) || Number(current.quantity) < share) fail('CART_CHANGED');
        if (own(updates, String(current.key))) fail('UNSUPPORTED_GPO_ALLOCATION');
        updates[String(current.key)] = Number(current.quantity) - share;
      });
    }
    validateUpdates(after, updates);
    return {
      version: 1,
      kind: 'commit',
      token,
      original: reference(original),
      updates,
      replacement: JSON.parse(JSON.stringify(replacement)),
      expected: expectedItems(after, updates)
    };
  }

  function planRollback(beforeCart, afterCart, originalKey, token) {
    const before = lines(beforeCart);
    const after = lines(afterCart);
    const original = originalLine(beforeCart, originalKey);
    if (tokenItems(before, token).length) fail('TOKEN_REUSED');
    if (!identify(afterCart, reference(original))) fail('ORIGINAL_MISSING');
    const added = tokenItems(after, token);
    if (added.some(line => group(line, CHILD) || String(line.product_id) !== String(original.product_id))) {
      fail('ROLLBACK_UNCERTAIN');
    }
    const groups = new Set(added.map(line => group(line, PARENT)).filter(Boolean));
    assertBaselineRetained(before, after, added, groups);
    const updates = Object.create(null);
    added.forEach(line => { updates[String(line.key)] = 0; });
    const prior = buckets(before);
    const current = buckets(after.filter(line => !added.includes(line)));
    current.forEach((bucket, key) => {
      if (!bucket.items.every(line => groups.has(group(line, CHILD)) && !group(line, PARENT))) return;
      const baseline = prior.get(key);
      let retain = baseline?.quantity || 0;
      const baselineKeys = new Map((baseline?.items || []).map(line => [String(line.key), Number(line.quantity)]));
      const ordered = bucket.items.slice().sort((a, b) => Number(baselineKeys.has(String(b.key))) - Number(baselineKeys.has(String(a.key))));
      ordered.forEach(line => {
        const keep = Math.min(Number(line.quantity), retain);
        retain -= keep;
        if (keep !== Number(line.quantity)) updates[String(line.key)] = keep;
      });
      if (retain !== 0) fail('ROLLBACK_UNCERTAIN');
    });
    validateUpdates(after, updates);
    return {
      version: 1,
      kind: 'rollback',
      token,
      original: reference(original),
      updates,
      replacement: added.length === 1 ? JSON.parse(JSON.stringify(added[0])) : null,
      expected: expectedItems(after, updates)
    };
  }

  function assertExpected(finalCart, expected) {
    const final = lines(finalCart);
    if (!Array.isArray(expected)) fail('INVALID_PLAN');
    const wanted = new Map();
    expected.forEach(entry => {
      if (!entry?.ref || !Number.isSafeInteger(entry.quantity) || entry.quantity < 0) fail('INVALID_PLAN');
      const key = fingerprint(entry.ref);
      wanted.set(key, (wanted.get(key) || 0) + entry.quantity);
    });
    const actual = buckets(final);
    wanted.forEach((quantity, key) => {
      if ((actual.get(key)?.quantity || 0) !== quantity) fail('VERIFY_FAILED');
    });
    actual.forEach((bucket, key) => {
      if (!wanted.has(key) || wanted.get(key) !== bucket.quantity) fail('VERIFY_FAILED');
    });
    return true;
  }

  function assertCommitted(beforeCart, finalCart, plan, originalKey) {
    const before = lines(beforeCart);
    const final = lines(finalCart);
    const original = originalLine(beforeCart, originalKey);
    if (!plan || plan.version !== 1 || plan.kind !== 'commit' || !Array.isArray(plan.expected) ||
        fingerprint(plan.original || {}) !== fingerprint(original) ||
        Number(plan.original.quantity) !== Number(original.quantity)) {
      fail('INVALID_PLAN');
    }
    if (tokenItems(before, plan.token).length) fail('TOKEN_REUSED');
    const added = tokenItems(final, plan.token);
    if (added.length !== 1 || fingerprint(added[0]) !== fingerprint(plan.replacement) ||
        Number(added[0].quantity) !== Number(original.quantity)) {
      fail('VERIFY_FAILED');
    }
    return assertExpected(finalCart, plan.expected);
  }

  return Object.freeze({ planCommit, planRollback, assertCommitted, assertExpected, identify, reference, signature, fingerprint, tokenProperty: TOKEN });
});


